import type { Automation, AutomationInput, AutomationTool, Schedule } from '@harness/shared/protocol';
import { useEffect, useId, useRef, useState } from 'react';
import { fetchAutomationTools } from '../api';
import styles from '../styles/automations.module.css';
import { approvalTitle, scheduleKindText, TEXT, weekdayName } from '../texts';

interface EditorProps {
  projectId: string;
  automation: Automation | null;
  onCancel: () => void;
  onSave: (input: AutomationInput) => Promise<void>;
}

const DEFAULT_SCHEDULE: Schedule = { kind: 'weekdays', time: '07:00' };

/** Escape closes a dialog; the list reloading behind it must not re-run the dialog's effects. */
function useEscape(onCancel: () => void): void {
  const latest = useRef(onCancel);
  latest.current = onCancel;
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') latest.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export function Editor({ projectId, automation, onCancel, onSave }: EditorProps): React.JSX.Element {
  const [name, setName] = useState(automation?.name ?? '');
  const [prompt, setPrompt] = useState(automation?.prompt ?? '');
  const [schedule, setSchedule] = useState<Schedule>(automation?.schedule ?? DEFAULT_SCHEDULE);
  const [allowed, setAllowed] = useState<string[]>(automation?.allowedTools ?? []);
  const [tools, setTools] = useState<AutomationTool[] | null>(null);
  const [saving, setSaving] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  const titleId = useId();

  useEscape(onCancel);
  useEffect(() => {
    first.current?.focus();
    fetchAutomationTools(projectId).then(setTools, () => setTools([]));
  }, [projectId]);

  // Tools allowed earlier stay visible even if the cached tool list no longer has them, so they can be removed.
  const offered = [...(tools ?? []).map((tool) => tool.tool), ...allowed.filter((tool) => !(tools ?? []).some((known) => known.tool === tool))];
  const toggle = (tool: string): void => setAllowed((current) => (current.includes(tool) ? current.filter((item) => item !== tool) : [...current, tool]));
  const valid = name.trim() !== '' && prompt.trim() !== '' && /^\d\d:\d\d$/.test(schedule.time);

  return (
    <div className={styles.backdrop}>
      <form
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(event) => {
          event.preventDefault();
          if (!valid || saving) return;
          setSaving(true);
          void onSave({ name: name.trim(), prompt: prompt.trim(), schedule, allowedTools: allowed }).finally(() => setSaving(false));
        }}
      >
        <h2 id={titleId} className={styles.dialogTitle}>
          {automation === null ? TEXT.newAutomation : TEXT.editAutomation}
        </h2>
        <label className={styles.field}>
          {TEXT.automationName}
          <input ref={first} value={name} maxLength={100} placeholder={TEXT.automationNamePlaceholder} onChange={(event) => setName(event.target.value)} />
        </label>
        <label className={styles.field}>
          {TEXT.automationPrompt}
          <textarea value={prompt} rows={5} placeholder={TEXT.automationPromptPlaceholder} onChange={(event) => setPrompt(event.target.value)} />
        </label>
        <fieldset className={styles.fieldset}>
          <legend>{TEXT.automationSchedule}</legend>
          <div className={styles.chips}>
            {(['daily', 'weekdays', 'weekly'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                className={styles.chip}
                aria-pressed={schedule.kind === kind}
                onClick={() => setSchedule(kind === 'weekly' ? { kind, weekday: schedule.kind === 'weekly' ? schedule.weekday : 1, time: schedule.time } : { kind, time: schedule.time })}
              >
                {scheduleKindText[kind]}
              </button>
            ))}
          </div>
          <div className={styles.row}>
            {schedule.kind === 'weekly' && (
              <label className={styles.inline}>
                {TEXT.automationWeekday}
                <select value={schedule.weekday} onChange={(event) => setSchedule({ ...schedule, weekday: Number(event.target.value) })}>
                  {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                    <option key={day} value={day}>
                      {weekdayName(day)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className={styles.inline}>
              {TEXT.automationTime}
              <input type="time" value={schedule.time} required onChange={(event) => setSchedule({ ...schedule, time: event.target.value })} />
            </label>
          </div>
        </fieldset>
        <fieldset className={styles.fieldset}>
          <legend>{TEXT.allowedTools}</legend>
          <p className={styles.hint}>{TEXT.allowedToolsHint}</p>
          {tools === null ? (
            <p className={styles.muted}>{TEXT.loading}</p>
          ) : offered.length === 0 ? (
            <p className={styles.muted}>{TEXT.noWritingTools}</p>
          ) : (
            <div className={styles.tools}>
              {offered.map((tool) => (
                <label key={tool} className={styles.tool}>
                  <input type="checkbox" checked={allowed.includes(tool)} onChange={() => toggle(tool)} />
                  {approvalTitle(tool)}
                </label>
              ))}
            </div>
          )}
        </fieldset>
        <div className={styles.dialogActions}>
          <button type="button" className={styles.secondary} onClick={onCancel}>
            {TEXT.cancel}
          </button>
          <button type="submit" className={styles.primary} disabled={!valid || saving}>
            {TEXT.save}
          </button>
        </div>
      </form>
    </div>
  );
}

interface ConfirmProps {
  title: string;
  text: string;
  confirm: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function Confirm({ title, text, confirm, onCancel, onConfirm }: ConfirmProps): React.JSX.Element {
  const cancel = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  useEscape(onCancel);
  useEffect(() => {
    cancel.current?.focus();
  }, []);
  return (
    <div className={styles.backdrop}>
      <div className={styles.confirmDialog} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId} className={styles.dialogTitle}>
          {title}
        </h2>
        <p className={styles.text}>{text}</p>
        <div className={styles.dialogActions}>
          <button ref={cancel} type="button" className={styles.secondary} onClick={onCancel}>
            {TEXT.cancel}
          </button>
          <button type="button" className={styles.confirm} onClick={onConfirm}>
            {confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
